import i18n from "app/libs/i18n";
import axios, { AxiosInstance } from "axios";

export class StrapiApi {
  private client: AxiosInstance;

  constructor(baseUrl: string, token?: string) {
    this.client = axios.create({
      baseURL: baseUrl,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
  }

  private getStrapiLocale() {
    const lang = (i18n.language || "").toLowerCase();

    if (["en", "eng", "us"].includes(lang)) return "en";
    if (["ru", "rus"].includes(lang)) return "ru";
    if (["am", "hy", "arm"].includes(lang)) return "hy";

    return "en";
  }

  async stories(userId: number, isIndividual: boolean) {
    try {
      const response = await this.client.post("/stories", {
        userId,
        isIndividual,
        locale: this.getStrapiLocale(),
      });
      return { data: response.data, error: null };
    } catch (error: any) {
      const resp = error?.response;
      if (!resp) {
        throw error;
      }
      return { data: null, error: resp.data };
    }
  }

  // Контентные запросы идут внутри loadResearch/loadBlog, которые держат
  // isLoading. Без таймаута зависший CMS оставил бы экран в вечной загрузке.
  private static readonly CONTENT_TIMEOUT = 10000;

  // Research ведётся в CMS только на английском, поэтому locale здесь
  // фиксированный, а не из getStrapiLocale().
  async researches() {
    try {
      const response = await this.client.get("/researches", {
        timeout: StrapiApi.CONTENT_TIMEOUT,
        params: {
          locale: "en",
          "sort[0]": "date:desc",
          "pagination[pageSize]": 100,
        },
      });
      return { data: response.data, error: null };
    } catch (error: any) {
      return { data: null, error: error?.response?.data ?? error };
    }
  }

  // Одна запись на локаль — вызывающий код собирает из трёх ответов привычный
  // MultiLangField. Армянский в CMS называется "hy", в приложении — "am".
  async blogs(locale: string, params?: Record<string, string | number>) {
    try {
      const response = await this.client.get("/blogs", {
        timeout: StrapiApi.CONTENT_TIMEOUT,
        params: { locale, "pagination[pageSize]": 200, ...params },
      });
      return { data: response.data, error: null };
    } catch (error: any) {
      return { data: null, error: error?.response?.data ?? error };
    }
  }

  // pdf отдаёт только этот эндпоинт — в списке его нет.
  async researchByAlias(alias: string) {
    try {
      const response = await this.client.get(`/research/${alias}`, {
        timeout: StrapiApi.CONTENT_TIMEOUT,
        params: { locale: "en" },
      });
      return { data: response.data, error: null };
    } catch (error: any) {
      return { data: null, error: error?.response?.data ?? error };
    }
  }
}
